import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { settingsBase } from './settingsPaths';
import '../tender/CustomerMaster.scss';

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const TimesheetRulesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  useDocumentTitle('Timesheet rules');

  useEffect(() => {
    platformAPI.getTimesheetSettings()
      .then((response) => {
        const settings = response.data?.data?.settings || {};
        setNote(settings.note || '');
        setForm(settings);
      })
      .catch((error) => setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load timesheet rules' }));
  }, []);

  const setField = (name) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await platformAPI.updateTimesheetSettings({
        ...form,
        dailyHoursLimit: Number(form.dailyHoursLimit),
        hourIncrement: Number(form.hourIncrement),
        backdatedDays: Number(form.backdatedDays),
        weekStartsOn: Number(form.weekStartsOn)
      });
      setForm(response.data?.data?.settings);
      setMessage({ variant: 'success', text: 'Timesheet rules saved for this workspace.' });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save timesheet rules' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <form className="cust-record" onSubmit={save}>
        <header className="cust-mast">
          <div>
            <button type="button" className="cust-page__back" onClick={() => navigate(settingsBase(location.pathname))}>
              <BiArrowBack />
              Settings
            </button>
            <h1>Timesheet rules</h1>
            <p className="text-muted">{note || 'Starting values for this workspace. They are not a confirmed company policy until you save them.'}</p>
          </div>
          <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </header>
        {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
        <section className="cust-section">
          <div className="cust-fields">
            <Field label="Daily hours limit" wide="4">
              <Form.Control type="number" min="0.5" max="24" step="0.5" value={form.dailyHoursLimit} onChange={setField('dailyHoursLimit')} />
            </Field>
            <Field label="Hour increment" wide="4">
              <Form.Control type="number" min="0.25" max="8" step="0.25" value={form.hourIncrement} onChange={setField('hourIncrement')} />
            </Field>
            <Field label="Backdated days" wide="4">
              <Form.Control type="number" min="0" max="3650" step="1" value={form.backdatedDays} onChange={setField('backdatedDays')} />
            </Field>
            <Field label="Week starts on" wide="4">
              <Form.Select value={form.weekStartsOn} onChange={setField('weekStartsOn')}>
                <option value="1">Monday</option>
                <option value="0">Sunday</option>
                <option value="6">Saturday</option>
              </Form.Select>
            </Field>
            <Field label="Future dates" wide="4">
              <Form.Check type="switch" label="Allow future work dates" checked={Boolean(form.allowFutureDates)} onChange={setField('allowFutureDates')} />
            </Field>
            <Field label="Self-approval" wide="4">
              <Form.Check type="switch" label="Allow a person to approve their own time" checked={Boolean(form.allowSelfApproval)} onChange={setField('allowSelfApproval')} />
            </Field>
            <Field label="Reopen" wide="12">
              <Form.Check type="switch" label="Authorised reviewers may reopen an approved entry when they give a reason" checked={form.allowReopen !== false} onChange={setField('allowReopen')} />
            </Field>
          </div>
          <p className="text-muted small mb-0">The approver is the assignment owner, then the tender owner, then another user who can approve timesheets. The person booking the time is skipped unless self-approval is on.</p>
        </section>
      </form>
    </div>
  );
};

export default TimesheetRulesPage;
